import * as fp from "lodash/fp.js";

export function map2Object<K extends string, V>(map: Map<K, V>) {
  return Object.fromEntries(map.entries()) as Partial<Record<K, V>>;
}

export function object2Map<K extends string, V>(object: Record<K, V>) {
  return new Map(Object.entries(object)) as Map<K, V>;
}

export function objectMap<K extends string, V, U>(
  iter: (value: V, key: K) => U,
  object?: { [key in K]: V }
) {
  if (!object) return undefined;
  return Object.keys(object).reduce((acc, _key) => {
    const key = _key as K;
    acc[key] = iter(object[key], key);
    return acc;
  }, {} as { [key in K]: U });
}

export const es6MapMap = <K extends string, V, U>(
  iter: (value: V, key: K, index: number) => U,
  map: Map<K, V>
) =>
  [...map].reduce((acc, [key, value], index) => {
    acc.set(key, iter(value, key, index));
    return acc;
  }, new Map<K, U>());

export const objectAssign = <T extends object, const K extends keyof T, U>(
  target: T,
  key: K,
  map: (value: T[K]) => U
) => {
  return fp.set(key, map(target[key]), target) as {
    [key in keyof T]: key extends K ? U : T[key];
  };
};

export const objectAdd = <T extends object, const K extends string, U>(
  target: T,
  key: K,
  value: U
) => {
  return fp.set(key, value, target) as T & { [key in K]: U };
};

export const groupBy = <T extends object, const K extends keyof T>(
  arr: T[],
  key: K | ((elem: T) => T[K])
) => {
  const grouped = new Map<T[K], T[]>();
  for (const elem of arr) {
    const val = typeof key === "function" ? key(elem) : elem[key];
    if (!val) continue;
    if (!grouped.has(val)) grouped.set(val, []);
    grouped.get(val)!.push(elem);
  }
  return grouped;
};

export const groupByPredicate = <T extends object, V extends string>(
  arr: T[],
  key: (elem: T) => V
) => {
  const grouped = new Map<V, T[]>();
  for (const elem of arr) {
    const val = typeof key === "function" ? key(elem) : elem[key];
    if (!val) continue;
    if (!grouped.has(val)) grouped.set(val, []);
    grouped.get(val)!.push(elem);
  }
  return grouped;
};

export const groupByUniq = <T extends object, const K extends keyof T>(
  arr: T[],
  key: K | ((elem: T) => T[K])
) => {
  const grouped = new Map<T[K], T>();
  for (const elem of arr) {
    const val = typeof key === "function" ? key(elem) : elem[key];
    if (!val) continue;
    grouped.set(val, elem);
  }
  return grouped;
};

export const partitionBy = <T extends object, const K extends keyof T>(
  arr: T[],
  predicate: K | ((elem: T) => boolean)
) => {
  const trueGroup: T[] = [];
  const falseGroup: T[] = [];
  for (const elem of arr) {
    const val =
      typeof predicate === "function" ? predicate(elem) : elem[predicate];
    if (val) trueGroup.push(elem);
    else falseGroup.push(elem);
  }
  return [trueGroup, falseGroup];
};
export class MapWithDefault<K, V> extends Map<K, V> {
  private defaultValue: () => V;

  constructor(defaultValue: () => V, entries?: [K, V][]) {
    super(entries);
    this.defaultValue = defaultValue;
  }

  get(key: K) {
    if (!this.has(key)) {
      this.set(key, this.defaultValue());
    }
    return super.get(key)!;
  }
}

export const pick = <T extends object, const K extends keyof T>(
  obj: T,
  keys: K[]
) => {
  return keys.reduce((acc, key) => {
    acc[key] = obj[key];
    return acc;
  }, {} as { [key in K]: T[key] });
};

export const omit = <T extends object, K extends keyof T>(
  obj: T,
  ...keys: K[]
): Omit<T, K> => {
  const _ = { ...obj };
  keys.forEach((key) => delete _[key]);
  return _;
};

export const objectEntries = <T extends object>(obj: T) =>
  Object.entries(obj) as [keyof T, T[keyof T]][];

export const entriesInOrder = <T extends object, const K extends keyof T>(
  obj: T,
  keys: K[]
) => {
  return keys.map((key) => [key, obj[key]] as const);
};

export const mapEntries = <K extends string, V, U>(
  map: Map<K, V>,
  iter: (value: V, key: K, index: number) => U
) => [...map.entries()].map(([key, value], index) => iter(value, key, index));

export const filterNonNull = <T>(x: T | null): x is T => {
  return x !== null;
};
export const filterNonUndefined = <T>(x: T | undefined): x is T => {
  return x !== undefined;
};
export const filterNonNullAndNonUndefined = <T>(x: T | null | undefined): x is T => {
  return x !== null && x !== undefined;
};
